import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Box } from '@mui/material';

import PageContainer from '../../components/layout/PageContainer';
import { clientActions, clientAssessmentActions, providerAssignmentActions } from '../../store';
import AssessmentInfoCard from '../../components/layout/supervision-notes/client-assessment/AssessmentInfoCard';
import { ClientAssessmentInfo } from '../../components/layout/supervision-notes/client-assessment/ClientAssessmentInfo';
import { ClientAssessmentForm } from '../../components/layout/supervision-notes/client-assessment/ClientAssessmentForm';
import { FProgressBar } from '../../components/base';
import { clientAssessmentReadOnly } from '../../utils/common';
import { areUserRolesIn } from '../../helpers';
import { ClinicalRelationships, Roles } from '../../constants';

import type { RootState } from '../../store';

export function AssessmentNote() {
  const { id: assessmentId } = useParams();
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();

  const { user } = useSelector((state: RootState) => state.auth);
  const { provider } = useSelector((state: RootState) => state.providers);
  const { loading: providerAssignmentLoading } = useSelector(
    (state: RootState) => state.providerAssignment,
  );
  const { current: clientAssessment, loading: clientAssessmentLoading } = useSelector(
    (state: RootState) => state.clientAssessment,
  );
  const { loading: clientLoading } = useSelector((state: RootState) => state.clients);

  const isReadOnlyView = useMemo(() => {
    return clientAssessmentReadOnly(clientAssessment, user);
  }, [clientAssessment, user]);

  useEffect(() => {
    if (!areUserRolesIn(user?.roles, [Roles.Bcba]) || !provider?.id) return;
    dispatch(
      providerAssignmentActions.getList({
        getAccessTokenSilently,
        active_only: true,
        provider_id: provider.id,
        clinical_relationship: [
          ClinicalRelationships.HomeBcba,
          ClinicalRelationships.BobbingBcba,
          ClinicalRelationships.SubstituteBCBA,
        ],
      }),
    );
  }, [provider?.id]);

  useEffect(() => {
    dispatch(clientActions.getList({ getAccessTokenSilently }));
    return () => {
      dispatch(clientActions.clearSelectedClient());
      dispatch(clientAssessmentActions.clearState());
      dispatch(providerAssignmentActions.clearAssignment());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isNaN(parseInt(String(assessmentId)))) {
      dispatch(clientAssessmentActions.clearState());
      return;
    }
    dispatch(
      clientAssessmentActions.getAssessmentNote({
        getAccessTokenSilently,
        assessmentNoteId: assessmentId,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentId]);

  return (
    <PageContainer
      dataTestId="assessmentNotesPageContainer"
      sx={{
        px: {
          xs: 2,
          sm: 3,
          md: 3,
          lg: 4,
        },
        mt: 3,
      }}
    >
      {(clientAssessmentLoading || clientLoading || providerAssignmentLoading) && <FProgressBar />}
      {isReadOnlyView ? (
        clientAssessment && <AssessmentInfoCard assessment={clientAssessment} />
      ) : (
        <ClientAssessmentInfo
          clientAssessment={clientAssessment ?? undefined}
          buttonDisabled={clientAssessmentLoading}
        />
      )}
      <Box sx={{ opacity: clientAssessment?.id ? 1 : 0.4 }}>
        <ClientAssessmentForm
          clientAssessment={clientAssessment ?? undefined}
          isReadOnlyView={isReadOnlyView ?? false}
        />
      </Box>
    </PageContainer>
  );
}
